// Date field — a compact input showing the selected date; clicking it opens the
// calendar (DatePicker) in a popover. Mirrors TimeSelect's ergonomics (button +
// backdrop + popover) so the Date and Time fields read as a matched pair.

import { memo, useCallback, useRef, useState } from 'react';

import { Popover } from '../ui/Popover';
import { IconCalendar, IconChevronDown } from '../ui/icons';
import { DatePicker, Direction } from './DatePicker';

const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MO = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function label(iso: string): string {
  if (!iso) return 'Pick a date';
  const d = new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return 'Pick a date';
  return `${WD[d.getDay()]}, ${MO[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

export const DateField = memo(function DateField({
  value,
  onChange,
  direction = 'future',
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  /** Passed through to the calendar: scheduling looks forward, history back. */
  direction?: Direction;
  /** Shown when nothing is chosen. "Pick a date" reads as an instruction; a
   * filter wants to say what it is doing instead ("Any date"). */
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const toggle = useCallback(() => setOpen((o) => !o), []);
  const close = useCallback(() => setOpen(false), []);
  const pick = useCallback(
    (v: string) => {
      onChange(v);
      setOpen(false);
    },
    [onChange],
  );

  return (
    <div className="bz-datefield">
      <button
        ref={btnRef}
        type="button"
        className="bz-datefield-btn"
        onClick={toggle}
        aria-expanded={open}
      >
        <IconCalendar size={15} />
        <span className="bz-datefield-val">
          {value ? label(value) : (placeholder ?? label(value))}
        </span>
        <IconChevronDown size={13} />
      </button>
      {/* NOT matchWidth: a calendar has an intrinsic size, and matching it to
          whatever button opened it meant the 7-column grid overflowed the popover
          the moment the trigger was narrow (the history filter pill). It only
          ever looked right because the schedule field happened to be wide. */}
      {open && (
        <Popover anchor={btnRef.current} onClose={close} className="bz-datefield-pop">
          <DatePicker value={value} onChange={pick} direction={direction} />
        </Popover>
      )}
    </div>
  );
});
